/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DestroyRef, Injectable} from '@angular/core';
import {firstValueFrom} from 'rxjs';
import {CreativeApiService} from '../creative/creative-api.service';
import _ from 'lodash';
import {MEDIA_TYPE, MediaTypeHelper} from '../../components/util/media-type-helper';
import {DEFAULT_LAYOUT_TYPES} from '../creative/creative-helper.service';
import {CreativeRetailProductModalService} from '../creative/creative-retail-product-modal.service';
import {ADN_TARGETING_TYPES} from '../targeting/targeting-constants';
import {ASPECT_RATIOS_TRANSLATIONS} from '../admin/layout/layout-commons';
import {resolveCreativePreviewAdvertiser} from '../creative/ad/preview-request-data';
import {requiresVideoPreviewParam} from '../creative/ad/preview-language';
import {advertiserFromNavigation} from '../../components/util/navigation-context';
import {formatMediaTypesTranslate} from '../common/filters/format-media-type';
import {SignalResourceListBindingService} from '../common/controller/signal-resource-list-binding.service';
import {attachEditPagePhase} from '../common/util/edit-page-phase';
import type {EditPagePhaseApi} from '../common/util/edit-page-phase';
import {NavigationHistoryService} from '../../components/util/navigation-history.service';
import {findCreativeAdvertiserMismatch} from '../creative/creative-advertiser-mismatch';
import {TeamApiService} from '../admin/team/team-api.service';
import {ProductFeedApiService} from '../common/controller/product-feed-api.service';
import {LibraryCreativeApiService} from './library-creative-api.service';
import type {LibraryCreativeEditPageComponent} from './library-creative-edit-page.component';

export interface LibraryCreativeEditSetupParams {
  model: any;
  readOnly: boolean;
  creativeType: any;
  lineItemInfo: any;
  navigationHistory: NavigationHistoryService;
  routeParams: any;
  translate: any;
  teamApi: TeamApiService;
  productFeedApi: ProductFeedApiService;
  CreativeHelper: any;
  listBinding: SignalResourceListBindingService;
  listLifetime: DestroyRef;
  Rollbar: any;
  Asset: any;
  Creative: CreativeApiService;
  LibraryCreative: LibraryCreativeApiService;
  libraryCreativeApi: LibraryCreativeApiService;
  LocalNetworkProfile: any;
  LocalUserPermissions: any;
  Advertiser: any;
  TargetingEditorModalService: any;
  creativeRetailProductModalService: CreativeRetailProductModalService;
}

export interface LibraryCreativeEditBehavior {
  afterInit(): void;
  afterInitAfterSave(): void;
  afterSave(creative: any): void;
  beforeSubmit(): void;
  destroy(): void;
}

@Injectable({providedIn: 'root'})
export class LibraryCreativeEditBehaviorService {
  initialize(page: LibraryCreativeEditPageComponent, params: LibraryCreativeEditSetupParams): LibraryCreativeEditBehavior {
    return configureLibraryCreativeEdit(page, params);
  }
}

function configureLibraryCreativeEdit(
  page: LibraryCreativeEditPageComponent,
  p: LibraryCreativeEditSetupParams
): LibraryCreativeEditBehavior {
  const {
    model, readOnly, lineItemInfo, navigationHistory, routeParams, translate, teamApi, productFeedApi,
    CreativeHelper, listBinding, Rollbar, Asset, Creative, LibraryCreative, libraryCreativeApi,
    LocalNetworkProfile, LocalUserPermissions, Advertiser, TargetingEditorModalService,
    creativeRetailProductModalService
  } = p;

  const editPhase: EditPagePhaseApi = attachEditPagePhase(page);
  const mInitPromises: any[] = [];
  const mLayoutAssetMatches: Record<string, any> = {};
  const defaultLayoutType = 'OTHER';
  const prevAssetAssignments: Record<string, any> = {};
  let mAllLayoutsPromise: any;
  let mPreviewAdvertiserPromise: any;
  let mPreviewAdvertiserId: any;
  let timeoutPromise: any;

  page.editsMade = () => page.markExternalFormDirty();
  page.updateModelHappened = () => page.publishModelUpdate();
  page.isCreative = false;
  page.skips = ['validation.warning.creative.blocked.by.scanner'];
  page.isPureMarketplace = LocalNetworkProfile.hasAdvertiserEnforced();
  page.aspectRatioDescriptions = ASPECT_RATIOS_TRANSLATIONS;
  page.defaultLayoutTypes = DEFAULT_LAYOUT_TYPES;
  page.layoutTypes = page.defaultLayoutTypes.concat(defaultLayoutType);
  page.perms = LocalUserPermissions.getAllVisPermissions();
  page.linkedCreativeFields = ['name', {param: 'LineItem', label: 'lineItem'}, {label: 'dimensions', param: 'width'}, {param: '', label: 'info'}, 'updateTime', {param: '', label: 'targeting'}];
  page.needsVerificationCheck = LocalNetworkProfile.isVerificationCheckNetwork();
  page.retailProductFields = {
    retailProductId: true,
    merchantId: true,
    title: true,
    body: true,
    image: true,
    clickUrl: true,
    sponsoredBy: true,
    priceCurrency: true,
    priceAmount: true,
    salePriceCurrency: true,
    salePriceAmount: true
  };
  page.layoutRef = {};

  const syncEditingModel = () => undefined;

  const teamParams: any = {minimal: true};
  if (page.isPureMarketplace) {
    teamParams.filterBy = 'type';
    teamParams.filterByValue = 'AX_ADVERTISER';
  }
  const teamQuery = firstValueFrom(teamApi.listOptions(teamParams)).then((teamPage) => {
    // undefined → [] flips [resolved] on adn-promise-spinner; defer to avoid NG0100.
    // Resolve teamQuery only once page.allTeams is actually set, since callers below
    // read page.allTeams synchronously after this promise settles.
    return new Promise<void>((resolve) => {
      editPhase.viewWrite(() => {
        page.allTeams = teamPage.results;
        resolve();
      });
    });
  });

  const initLinkedCreatives = () => {
    if (page.isNew || !page.editingModel?.id || page.listState) return;
    listBinding.create(Creative, 'fullCreativesforLibraryCreative', p.listLifetime, {
      moreParams: {libraryCreative: page.editingModel.id},
      baseTable: 'creative'
    }, page);
  };

  const updateDefaultName = () => {
    CreativeHelper.updateDefaultName(page);
  };

  const hydrateSelectedRetailProduct = (productId: string, merchantId: string) => {
    if (!productId || !merchantId) {
      page.selRetailProduct = null;
      return Promise.resolve(null);
    }
    return firstValueFrom(productFeedApi.get(productId, merchantId)).then((retailProd: any) => {
      editPhase.viewWrite(() => {
        page.selRetailProduct = retailProd;
        page.merchantGuide = page.merchantGuide || {};
        page.selRetailProduct.hydratedMerchant = page.merchantGuide[page.selRetailProduct.merchantId];
      });
      return retailProd;
    });
  };

  const applyRetailCreativeUpdate = (creative: any) => {
    syncEditingModel();
    page.editingModel = {...creative};
    const hydratedLayout = page.layoutRef[_.get(page.editingModel, ['layout', 'id'])];
    if (hydratedLayout) {
      page.editingModel.layout = hydratedLayout;
    }
    page.applyBehaviorSave(creative);
  };

  const ensureRetailCreativeIsSaved = () => {
    if (!page.isNew) {
      return Promise.resolve(page.editingModel);
    }
    page.prepareLibraryCreativeForSubmit();
    return firstValueFrom(libraryCreativeApi.save(page.editingModel)).then((savedCreative: any) => {
      page.editingModel.id = savedCreative.id;
      _.set(page.editingModel, 'validationWarnings', savedCreative.validationWarnings);
      return savedCreative;
    });
  };

  const getCurrentPreviewAdvertiser = () => {
    const modelAdvertiserId = _.get(page.editingModel, ['advertiser', 'id']);
    if (_.get(page.advertiserInfo, 'id') && _.get(page.advertiserInfo, 'id') === modelAdvertiserId) {
      return page.advertiserInfo;
    }
    return page.editingModel.advertiser || page.advertiserInfo || null;
  };

  const getAdvertiserForPreview = () => {
    const advertiser = getCurrentPreviewAdvertiser();
    return Promise.resolve(resolveCreativePreviewAdvertiser(page.editingModel, null, advertiser, (advertiserId: string) => {
      if (_.get(page.advertiserInfo, 'id') === advertiserId && _.get(page.advertiserInfo, 'legalName')) {
        return page.advertiserInfo;
      }
      if (mPreviewAdvertiserPromise && mPreviewAdvertiserId === advertiserId) {
        return mPreviewAdvertiserPromise;
      }
      mPreviewAdvertiserId = advertiserId;
      mPreviewAdvertiserPromise = firstValueFrom(Advertiser.get(advertiserId)).then((data: any) => {
        page.advertiserInfo = data;
        return data;
      }, () => advertiser);
      return mPreviewAdvertiserPromise;
    }));
  };

  const updateCreativeDims = () => {
    syncEditingModel();
    CreativeHelper.updateCreativeDims(page, prevAssetAssignments);
  };

  const checkHtmlUrls = () => {
    syncEditingModel();
    CreativeHelper.checkHtmlUrls(page);
  };

  const matchConstraintsAndAssets = (newAssetUpload?: boolean) => {
    syncEditingModel();
    return CreativeHelper.matchConstraintsAndAssets(page, mInitPromises, mLayoutAssetMatches, prevAssetAssignments, delayedPreview, newAssetUpload);
  };

  const callForPreview = () => {
    Promise.all([Promise.resolve(mAllLayoutsPromise)]).then(() => getAdvertiserForPreview()).then((previewAdvertiser: any) => {
      syncEditingModel();
      const previewResults = (failureReason: string | null, previewData?: any) => {
        // Defer — selPreview flips [hidden]="!selPreview" true→false mid-CD (NG0100).
        editPhase.viewWrite(() => {
          page.selPreviewFail = failureReason;
          page.selPreview = previewData;
          const selLayoutType = _.get(page.editingModel, ['layout', 'type'], 'unspecified');
          const key = page.defaultLayoutTypes.indexOf(selLayoutType) > -1 && selLayoutType !== 'HTML' ? 'style1' : 'style2';
          if (previewData && page.selPreviewTrigger[key].previewCreative) {
            page.selPreviewTrigger[key].previewCreative(previewData);
          }
        });
      };

      if (!page.editingModel.layout || !page.editingModel.layout.id) {
        previewResults('A valid layout needs to be selected before a creative can be previewed.');
        return;
      }

      const adServingWarnings = _.find(page.editingModel.layout.validationWarnings, (v: any) => v.preventsAdServing);
      if (adServingWarnings) {
        previewResults('The select layout is invalid and cannot be used to preview a creative.');
        return;
      }

      const creativeAssets = page.editingModel.assets.concat(page.editingModel.libraryCreativeAssets || []);
      const availableAssets = _.map(creativeAssets, (asset: any) => _.omit(asset, ['matchingAcs', 'assetComponents']));
      if (availableAssets.length === 0 && _.filter(page.editingModel.layout.assetComponents, ['required', 'MANDATORY']).length > 0) {
        previewResults('Mandatory assets must be made available before a creative can be previewed.');
        return;
      }

      const assets = _.filter(availableAssets, (asset: any) => {
        const assetIds = _.keyBy(_.valuesIn(page.editingModel.cToAssets), 'id');
        return assetIds[asset.id];
      });
      const text = _.filter(page.editingModel.cToText, (v: any) => !!v);
      const links = _.filter(page.editingModel.cToUrls, (v: any) => !!v);
      const choices = _.filter(page.editingModel.cToChoices, (v: any) => !!v);
      const mandatoryCompLength = _.filter(_.get(page.editingModel, ['layout', 'layoutComponents'], []), (lc: any) => lc.required === 'MANDATORY').length;
      if (mandatoryCompLength > 0 && assets.length === 0 && text.length === 0 && links.length === 0 && choices.length === 0) {
        previewResults('At least one layout component needs an asset or text assigned to it before a creative can be previewed.');
        return;
      }

      if (page.editingModel.dimensionType === 'FIXED' && (!page.editingModel.width || !page.editingModel.height)) {
        previewResults('The creative must have a width and height before it can be previewed.');
        return;
      }
      if (page.editingModel.dimensionType === 'FIXED_HEIGHT' && !page.editingModel.height) {
        previewResults('The creative must have a height before it can be previewed.');
        return;
      }
      if (page.editingModel.dimensionType === 'FIXED_WIDTH' && !page.editingModel.width) {
        previewResults('The creative must have a width before it can be previewed.');
        return;
      }

      const creativeModel = _.cloneDeep(page.editingModel);
      CreativeHelper.doLayoutParams(page, creativeModel);
      previewResults(null, {
        layoutId: page.editingModel.layout.id,
        creative: creativeModel,
        lineItem: null,
        advertiser: previewAdvertiser,
        election: null,
        video: requiresVideoPreviewParam(page.editingModel)
      });
    });
  };

  const delayedPreview = () => {
    clearTimeout(timeoutPromise);
    timeoutPromise = setTimeout(() => callForPreview(), 1500);
  };

  const overridableTargetingForCtrl = () => {
    page.tOverrides = {};
    page.tOverridesTypes = [];
    _.forEach(ADN_TARGETING_TYPES, (val: any) => {
      const ot = _.find(page.editingModel.overridableTargeting, (tt: string) => tt === val.apiType);
      if (ot) {
        page.tOverridesTypes.push(val.id);
      }
      page.tOverrides[val.apiType] = !!ot;
    });
  };

  const checkShowGeneralVariables = () => {
    page.showGeneralVariables = _.get(page, ['editingModel', 'layout', 'layoutType']) === 'THIRD_PARTY' || !!_.find(page.allComponents || [], (c: any) => c.type === 'TEXT' && c.textType === 'CODE');
  };

  const afterInitAfterSave = () => {
    initLinkedCreatives();
    syncEditingModel();
    // Save response replaces layout with a stub {id}; restore assetComponents etc. before CD.
    const layoutId = _.get(page.editingModel, ['layout', 'id']);
    const layoutFromRef = layoutId && page.layoutRef[layoutId];
    if (layoutFromRef) {
      page.editingModel.layout = layoutFromRef;
    }
    page.hasLibraryCreative = false;
    page.lcOverrides = {};
    overridableTargetingForCtrl();
    page.dimensionOverrides = false;
    page.savedTeams = _.clone(page.editingModel.teams);

    page.advertiserMismatch = findCreativeAdvertiserMismatch(page.editingModel.validationWarnings);

    Promise.resolve(mAllLayoutsPromise).then(() => {
      const hydratedLayout = page.layoutRef[_.get(page.editingModel, ['layout', 'id'])];
      if (hydratedLayout) {
        page.editingModel.layout = hydratedLayout;
      }
      const productId = _.get(page.editingModel, ['layoutParameters', 'retailProductId']);
      const merchantId = _.get(page.editingModel, ['layoutParameters', 'merchantId']);
      if (hydratedLayout && hydratedLayout.detectedCategory === 'RETAIL' && productId && merchantId) {
        hydrateSelectedRetailProduct(productId, merchantId);
        const notRetailComponents = _.filter(page.layoutRef[page.editingModel.layout.id].layoutComponents, (lc: any) => !page.retailProductFields[lc.tag]);
        page.isPureRetailCreative = page.layoutRef[page.editingModel.layout.id].layoutType === 'RETAIL' && !notRetailComponents.length;
      }
    });

    if (page.isPureMarketplace) {
      Promise.resolve(teamQuery).then(() => {
        page.editingModel.singularTeam = page.editingModel.teams[0];
        if (!page.editingModel.singularTeam && page.allTeams.length === 1) {
          page.editingModel.singularTeam = _.get(page, ['allTeams', 0, 'id']);
        }
      });
    }
    checkShowGeneralVariables();
  };

  const afterInit = () => {
    editPhase.wait('init');
    syncEditingModel();
    page.editingModel = {...page.editingModel};

    const axPublisherPermission = page.editingModel.permission === 'AX_PUBLISHER';
    page.readOnlyAxPublisher = axPublisherPermission && !page.editingModel.targeting;
    page.showTargeting = !axPublisherPermission || page.editingModel.targeting;
    page.blockSubmit = readOnly || page.blockSubmit || page.readOnlyAxPublisher;

    page.editingModel.targeting = page.editingModel.targeting || {};
    page.editingModel.cToAssets = page.editingModel.cToAssets || {};
    page.editingModel.userState = page.editingModel.userState || 'APPROVED';
    page.editingModel.type = page.editingModel.type || 'INTERNAL';
    page.htmlAcWithAssetMatches = {};
    page.selPreviewTrigger = {style1: {}, style2: {}};
    page.editingModel.teams = _.map(page.editingModel.teams, 'id');
    page.editingModel.dimensionType = page.editingModel.dimensionType || 'FIXED';
    page.editingModel.renderOption = page.editingModel.renderOption || null;

    const advertiser = advertiserFromNavigation(navigationHistory, routeParams);
    if (advertiser) {
      page.editingModel.advertiser = advertiser;
      page.advertiserSpecified = true;
    }

    if (!mAllLayoutsPromise) {
      mAllLayoutsPromise = CreativeHelper.getLayouts(page, defaultLayoutType, null);
    }
    mInitPromises.push(mAllLayoutsPromise);

    if (page.editingModel.id) {
      page.models = [page.editingModel];
      Promise.resolve(mAllLayoutsPromise).then(() => {
        if (page.editingModel.layout) {
          const hydratedLayout = _.find(page.layouts, ['id', page.editingModel.layout.id]);
          if (hydratedLayout) {
            page.editingModel.layout = hydratedLayout;
          }
        }
      });
    }

    Promise.all(mInitPromises).then(() => {
      return Promise.resolve(matchConstraintsAndAssets()).then(() => {
        delayedPreview();
        checkShowGeneralVariables();
      });
    }).then(() => {
      editPhase.done('init');
    }, () => {
      editPhase.done('init');
    });

    afterInitAfterSave();
  };

  const afterSave = (creative: any) => {
    syncEditingModel();
    page.targetingResult = {data: false};
    page.editingModel.targeting = creative.targeting || {};
    page.teamsWarning = (_.get(page.editingModel, 'teams') || []).length !== (_.get(creative, 'teams') || []).length;
    _.set(page.editingModel, 'teams', _.map(_.get(creative, 'teams') || [], 'id'));
  };

  page.lineItemInfo = lineItemInfo || {};

  page.assetAssignmentChanged = (tag: string) => {
    syncEditingModel();
    const selectedAsset = _.get(page.editingModel, 'cToAssets.' + tag);
    if (selectedAsset && MediaTypeHelper.isMediaType(selectedAsset.mimeType, MEDIA_TYPE.html) && page.primaryHtmlAsset) {
      page.primaryHtmlAsset = selectedAsset;
      checkHtmlUrls();
    }
    updateCreativeDims();
    delayedPreview();
    updateDefaultName();
    if (_.isFunction(page.eventHook.onAssetUpdate)) {
      page.eventHook.onAssetUpdate();
    }
  };

  page.selectTeams = () => {
    page.editsMade();
    page.editingModel.teams = _.map(page.allTeams, 'id');
  };

  page.deselectTeams = () => {
    page.editsMade();
    page.editingModel.teams = [];
  };

  page.isTeamSelected = (teamId: string) => (page.editingModel.teams || []).indexOf(teamId) > -1;

  page.toggleTeam = (teamId: string) => {
    page.editsMade();
    const teams = page.editingModel.teams || [];
    const idx = teams.indexOf(teamId);
    if (idx > -1) {
      teams.splice(idx, 1);
    } else {
      teams.push(teamId);
    }
    page.editingModel.teams = teams;
  };

  page.selectLayout = (layout?: any) => {
    syncEditingModel();
    if (layout) {
      page.editingModel.layout = layout;
    }
    page.editingModel.cToAssets = {};
    // ngModelChange can fire with null when the layout select is cleared.
    if (!page.editingModel.layout) {
      page.editingModel.defaultLayoutWidth = null;
      page.editingModel.defaultLayoutHeight = null;
      return;
    }
    if (page.editingModel.defaultLayoutWidth === page.editingModel.width && page.editingModel.defaultLayoutHeight === page.editingModel.height) {
      page.editingModel.width = page.editingModel.layout.defaultWidth;
      page.editingModel.height = page.editingModel.layout.defaultHeight;
    }
    page.editingModel.width = page.editingModel.width || page.editingModel.layout.defaultWidth;
    page.editingModel.height = page.editingModel.height || page.editingModel.layout.defaultHeight;
    page.editingModel.defaultLayoutWidth = page.editingModel.layout.defaultWidth;
    page.editingModel.defaultLayoutHeight = page.editingModel.layout.defaultHeight;
    Promise.resolve(matchConstraintsAndAssets(true)).then(() => {
      checkShowGeneralVariables();
      callForPreview();
    });
  };

  page.eventHook = {
    onUpdate: (uploadResponse?: any) => {
      page.editsMade();
      return Promise.resolve(matchConstraintsAndAssets(true)).then(() => {
        syncEditingModel();
        if (CreativeHelper.updateCreativeDimsFromVideoUpload(page, uploadResponse)) {
          delayedPreview();
        }
        editPhase.viewWrite(() => undefined);
      });
    },
    assignAsset: (asset: any, tag: string) => {
      page.editsMade();
      page.editingModel.cToAssets[tag] = asset;
      updateCreativeDims();
      delayedPreview();
      updateDefaultName();
    }
  };

  page.componentValueUpdated = () => {
    page.editsMade();
    delayedPreview();
  };

  page.dimensionsChange = () => {
    delayedPreview();
  };

  page.editTarget = () => {
    page.targetingResult = {data: false};
    const modelLineItem: any = {creativesForReach: [page.editingModel], fromCreative: true};
    TargetingEditorModalService.open({
      lineItemType: 'NORMAL',
      modelLineItem: modelLineItem,
      pageType: 'creative',
      reachEnabled: 'CREATIVE',
      targetingMode: null,
      overridableTargeting: null,
      mandatoryTargets: undefined,
      availableTargets: undefined,
      targeting: _.cloneDeep(page.editingModel.targeting),
      isLockedDown: false
    }).then((wc: any) => {
      if (!wc) {
        return;
      }
      _.assign(page.editingModel.targeting, wc.wc);
      page.editingModel.targeting = _.cloneDeep(page.editingModel.targeting);
      if (!page.isNew) {
        firstValueFrom(libraryCreativeApi.savePartial(page.editingModel, ['targeting'])).then((respCreative: any) => {
          editPhase.viewWrite(() => {
            page.targetingResult = {data: true};
            _.set(page.editingModel, 'validationWarnings', respCreative.validationWarnings);
            _.set(page.editingModel, 'targeting', respCreative.targeting);
          });
        }, (err: any) => {
          editPhase.viewWrite(() => {
            page.targetingResult = {
              data: false,
              errors: _.get(err, ['data', 'errors'], [])
            };
          });
          Rollbar.error('Library creative targeting problem', err);
        });
      } else {
        page.editsMade();
      }
    }, () => undefined);
  };

  page.refreshCodeMirrorVar = false;
  page.refreshCodeMirror = () => {
    setTimeout(() => {
      page.refreshCodeMirrorVar = true;
    }, 100);
  };

  const beforeSubmit = () => {
    syncEditingModel();
    _.forEach(_.keysIn(page.editingModel.cToAssets), (t: string) => {
      if (!page.editingModel.cToAssets[t]) {
        delete page.editingModel.cToAssets[t];
      }
    });

    page.editingModel.overridableLayoutParameters = [];
    page.editingModel.overrideFields = [];

    if (page.editingModel.dimensionType === 'FLEXIBLE') {
      page.editingModel.width = null;
      page.editingModel.height = null;
    } else if (page.editingModel.dimensionType === 'FIXED_WIDTH') {
      page.editingModel.height = null;
    } else if (page.editingModel.dimensionType === 'FIXED_HEIGHT') {
      page.editingModel.width = null;
    }

    CreativeHelper.doLayoutParams(page);
    if (_.isEmpty(page.editingModel.targeting)) {
      delete page.editingModel.targeting;
    }
    if (page.primaryHtmlAsset) {
      Asset.update(page.editingModel.id, {htmlUrls: page.primaryHtmlAsset.htmlUrls}, page.primaryHtmlAsset.id);
    }

    if (page.isPureMarketplace) {
      page.editingModel.teams = page.editingModel.singularTeam ? [page.editingModel.singularTeam] : null;
    }
  };

  page.createRetailCreative = () => {
    page.editsMade();
    const merchantGuide = page.merchantGuide || {};
    creativeRetailProductModalService.open({
      selRetailProduct: page.selRetailProduct,
      onSelect: (product: any) => {
        const merchantLink = _.get(merchantGuide, [product.merchantId, 'link']);
        editPhase.viewWrite(() => {
          page.setExternalPending(true);
        });
        return ensureRetailCreativeIsSaved().then((savedCreative: any) => {
          const creativeId = _.get(savedCreative, 'id') || _.get(page.editingModel, 'id') || '';
          return firstValueFrom(Creative.retailAd(null, creativeId, _.get(page.editingModel, ['layout', 'id']), merchantLink, product.itemId));
        })
          .then((creative) => firstValueFrom(Creative.hydrate(creative)))
          .then((creative: any) => {
            applyRetailCreativeUpdate(creative);
            return hydrateSelectedRetailProduct(product.itemId, product.merchantId);
          })
          .finally(() => {
            editPhase.viewWrite(() => {
              page.setExternalPending(false);
            });
          });
      }
    });
  };

  page.filterAssets = (assets: any[]) => {
    return _.filter(assets || [], (a: any) => a.objectState === 'ACTIVE' && (!a.htmlCreativeType || MediaTypeHelper.isMediaType(a.mimeType, MEDIA_TYPE.html)));
  };

  page.translateMediaTypes = (mimeTypes: any) => {
    return formatMediaTypesTranslate(mimeTypes, (key) => translate.instant(key));
  };

  return {
    afterInit,
    afterInitAfterSave,
    afterSave,
    beforeSubmit,
    destroy: () => clearTimeout(timeoutPromise)
  };
}

export function assetFilter(assets: any[]): any[] {
  return _.filter(assets || [], (a: any) => a.objectState === 'ACTIVE' && (!a.htmlCreativeType || MediaTypeHelper.isMediaType(a.mimeType, MEDIA_TYPE.html)));
}
